import { Box } from '@doltech/ui/lib/figma/Common/Box';
import { DragIn } from '@doltech/ui/lib/figma/DesignSystemV2/DragIn';
import {
  DragOutMobile,
  DragOutMobileResult,
} from '@doltech/ui/lib/figma/DesignSystemV2/DragOutMobile';
import { MarkedTypeEnum, TestModeEnum } from '@doltech/utils/lib/constants';
import { isNumber } from '@doltech/utils/lib/number';
import { numberToABC } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ResultDragInQuestionInstance } from '../../model/Question/result-drag-in-question.store';
import { useTestResultStore } from '../../model/test-result.hook';
import { ExtraQuestionProps } from './BaseQuestion';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { shadows } from '@doltech/ui/lib/figma/shadows';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';

interface DragInResultProps extends React.HTMLAttributes<HTMLDivElement> {
  data: ResultDragInQuestionInstance & ExtraQuestionProps;
}

export const DragInQuestionResult = observer((props: DragInResultProps) => {
  const store = useTestResultStore();
  const { data, ...rest } = props;
  const { options, image, no, index, userAnswers, marked, correctOptions, questions, scroll } =
    data;

  let defaultMarkedType = null;
  if (store.testMode === TestModeEnum.EXPLANATION) {
    defaultMarkedType = MarkedTypeEnum.CORRECT;
  }

  const results = React.useMemo(
    () =>
      questions.map((question, idx) => {
        const markedType = defaultMarkedType || marked[idx];
        const optionIdx = correctOptions[idx].key;
        const userAnswerKey = userAnswers[idx];
        const number = no + idx - index;

        return {
          ...question,
          number,
          userAnswer: isNumber(userAnswerKey) ? numberToABC(userAnswerKey + 1) : undefined,
          userText: isNumber(userAnswerKey) ? options[userAnswerKey] : undefined,
          correctText: options[optionIdx],
          correctAnswer: numberToABC(optionIdx + 1),
          markedType,
          isWatching: index === idx,
          goHere: () => {
            store.goToQuestion(number, scroll);
          },
        };
      }),
    [
      userAnswers,
      questions,
      marked,
      no,
      correctOptions,
      options,
      index,
      store,
      scroll,
      defaultMarkedType,
    ]
  );

  return (
    <Main className="dol-question drag-in-answer" {...rest}>
      <div className="image-wrapper">
        <Img
          loading="lazy"
          alt="Drag in result image"
          src={image.url}
          style={{ width: image.width, height: image.height }}
        />
        {results.map(
          ({ number, x, y, id, userAnswer, isWatching, markedType, goHere, correctAnswer }) => (
            <Spot style={{ left: x, top: y }} key={id}>
              <DragIn
                onClick={goHere}
                number={number}
                className={cl({
                  empty: markedType === MarkedTypeEnum.MISSING,
                  'no-focus': !isWatching,
                  'in-correct': isWatching && markedType === MarkedTypeEnum.WRONG,
                  missed: isWatching && markedType === MarkedTypeEnum.MISSING,
                  correct: isWatching && markedType === MarkedTypeEnum.CORRECT,
                })}
                answer={markedType === MarkedTypeEnum.CORRECT ? correctAnswer : userAnswer}
              />
            </Spot>
          )
        )}
      </div>

      <Results>
        {results.map(
          ({
            number,
            id,
            userAnswer,
            markedType,
            correctText,
            correctAnswer,
            isWatching,
            userText,
            goHere,
          }) =>
            isWatching ? (
              <DragOutMobileResult
                onClick={goHere}
                key={id}
                number={number}
                type={markedType}
                correctAnswer={correctAnswer}
                correctText={correctText}
                userText={userText}
                userAnswer={userAnswer}
                testMode={store.testMode}
              />
            ) : (
              <DragOutMobile
                onClick={goHere}
                number={number}
                className={cl('no-focus', { empty: userAnswer === undefined })}
                answer={userAnswer}
                text={correctText}
                key={id}
              />
            )
        )}
      </Results>
    </Main>
  );
});

export const Results = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

export const Spot = styled.span`
  position: absolute;
  line-height: 0;
  div {
    margin-right: unset;
  }
  > span {
    padding: 6px 12px;
    margin: 0;
  }
`;

export const Img = styled.img``;

export const Main = styled(Box)`
  display: grid;
  .image-wrapper {
    position: relative;
    margin: 0 auto;
  }

  background-color: ${colorsV2.white100};
  box-shadow: ${shadows.neutralShadowToBot1};
  padding: 16px 12px 20px;

  ${fromScreen(769)} {
    padding: 20px 16px;
    border-radius: 16px;
    border: 1px solid ${colorsV2.neutral20};
  }
`;
